import { createContext, useContext, useEffect, useState } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';

import { dsTokens, type ColorMode } from '@/shared/theme/tokens';

type Theme = ColorMode;

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

// v2 (2026-08-19) : nouvelle clé pour re-défauter tout le monde sur dark —
// l'ancienne clé 'theme' contenait 'light' auto-persisté à chaque chargement,
// impossible à distinguer d'un choix volontaire.
const STORAGE_KEY = 'theme.v2';
const DEFAULT_THEME: Theme = 'dark';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : DEFAULT_THEME;
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-color-mode', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const skeletonColors = theme === 'dark'
    ? { baseColor: dsTokens.dark['surface.subtle'], highlightColor: dsTokens.dark['surface.sunken'] }
    : { baseColor: dsTokens.light['surface.sunken'], highlightColor: dsTokens.light['surface.subtle'] };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      <SkeletonTheme baseColor={skeletonColors.baseColor} highlightColor={skeletonColors.highlightColor}>
        {children}
      </SkeletonTheme>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
